import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { PasswordField } from './PasswordField';

/**
 * Mirrors `assertPasswordStrength` on the server.
 *
 * The server stays the authority — this only spares someone a round trip and a
 * cleared form to be told a rule that could have been stated up front. Keep the
 * two in step: a client rule that is *looser* is merely annoying, but one that
 * is stricter silently forbids passwords the product actually accepts.
 */
export function passwordProblem(password: string): string {
  if (password.length < 10) return 'رمز عبور باید حداقل ۱۰ نویسه باشد.';
  if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) return 'رمز عبور باید هم حرف و هم رقم داشته باشد.';
  return '';
}

/**
 * Changing your own password. Used both from settings and from the forced
 * screen a temporary password lands on.
 *
 * The server ends every session for the account on success — that is the point
 * of a password change — so there is no "saved" state to return to here. The
 * caller is expected to send the person back to the sign-in screen.
 */
export function ChangePasswordForm({ onChanged }: { onChanged: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const problem = passwordProblem(next);
    if (problem) { setError(problem); return; }
    if (next !== repeat) { setError('رمز جدید و تکرار آن یکسان نیست.'); return; }
    if (next === current) { setError('رمز جدید باید با رمز فعلی فرق داشته باشد.'); return; }

    setBusy(true);
    setError('');
    try {
      await api.changeOwnPassword(current, next);
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'رمز عبور عوض نشد.');
      // Only the current-password field is cleared: when the failure is a wrong
      // current password, retyping a new password they had already chosen is
      // busywork that invites them to pick something shorter.
      setCurrent('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form-grid" onSubmit={submit}>
      <PasswordField
        id="password-current"
        label="رمز عبور فعلی"
        autoComplete="current-password"
        value={current}
        onValueChange={setCurrent}
      />
      <PasswordField
        id="password-next"
        label="رمز عبور جدید"
        hint="حداقل ۱۰ نویسه، شامل حرف و رقم."
        value={next}
        onValueChange={setNext}
      />
      <PasswordField
        id="password-repeat"
        label="تکرار رمز عبور جدید"
        value={repeat}
        onValueChange={setRepeat}
      />
      {error && <p className="form-error span-full" role="alert">{error}</p>}
      <div className="span-full">
        <button className="button button--primary" type="submit" disabled={busy}>
          {busy ? 'در حال ثبت…' : 'ثبت رمز جدید'}
        </button>
        <p className="hint">پس از تغییر رمز، از همهٔ دستگاه‌ها خارج می‌شوید و باید دوباره وارد شوید.</p>
      </div>
    </form>
  );
}
